import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserSubscriptionHistoryItemDto } from '@workspace/data-types';
import {
  AlertSnackBarService,
  StripePricingTableComponent,
  UserSubscriptionCardAuraComponent,
  UserSubscriptionHistoryAuraComponent,
  UserSubscriptionPortalCardAuraComponent,
} from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, tap } from 'rxjs';

@Component({
  selector: 'core-user-subscription',
  templateUrl: './user-subscription.component.html',
  imports: [
    StripePricingTableComponent,
    UserSubscriptionCardAuraComponent,
    UserSubscriptionHistoryAuraComponent,
    UserSubscriptionPortalCardAuraComponent,
  ],
  host: {
    class: 'block w-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSubscriptionCoreComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private userStore = inject(UserStore);
  private apiService = inject(ApiService);
  private alertSnackbar = inject(AlertSnackBarService);

  title = input<string>('Abonnement et facturation');
  loadingPortal = signal<boolean>(false);
  loadingHistory = signal<boolean>(false);
  loadingMore = signal<boolean>(false);
  hasMore = signal<boolean>(false);
  total = signal<number>(0);
  page = signal<number>(0);
  readonly pageSize = 5;

  history = signal<UserSubscriptionHistoryItemDto[]>([]);

  currentUser = this.userStore.currentUser;

  subscription = computed(() => this.currentUser()?.subscription);

  isSubscribed = computed(() => {
    const sub = this.subscription();
    if (!sub) return false;
    return sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due';
  });

  planName = computed(() => {
    const plan = this.subscription()?.plan;
    if (typeof plan === 'object' && plan !== null) {
      const p = plan as Record<string, unknown>;
      if (typeof p['slug'] === 'string' && p['slug']) {
        return p['slug'].charAt(0).toUpperCase() + p['slug'].slice(1);
      }
      if (typeof p['name'] === 'string' && p['name']) {
        return p['name'];
      }
    }
    return 'Premium';
  });

  ngOnInit(): void {
    this.loadHistory(0, true);
  }

  loadHistory(page = 0, isInitial = true): void {
    if (isInitial) {
      this.loadingHistory.set(true);
    } else {
      this.loadingMore.set(true);
    }

    this.apiService
      .user()
      .subscription()
      .getHistory(page, this.pageSize)
      .pipe(
        tap((res) => {
          if (page === 0) {
            this.history.set(res?.items || []);
          } else {
            this.history.update((prev) => [...prev, ...(res?.items || [])]);
          }
          this.total.set(res?.total || 0);
          this.hasMore.set(res?.hasMore || false);
          this.page.set(page);
        }),
        catchError(() => EMPTY),
        finalize(() => {
          this.loadingHistory.set(false);
          this.loadingMore.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadMoreHistory(): void {
    if (this.loadingHistory() || this.loadingMore() || !this.hasMore()) {
      return;
    }
    this.loadHistory(this.page() + 1, false);
  }

  manageSubscription() {
    this.loadingPortal.set(true);
    const returnUrl = window.location.href;

    this.apiService
      .user()
      .subscription()
      .createPortalSession(returnUrl)
      .pipe(
        tap((res) => {
          if (res?.url) {
            window.location.href = res.url;
          } else {
            this.alertSnackbar.error({
              title: 'Erreur de facturation',
              message: 'Lien de redirection invalide.',
            });
          }
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            title: 'Erreur de facturation',
            message: err.error?.message || 'Impossible de charger votre espace de facturation Stripe.',
          });
          return EMPTY;
        }),
        finalize(() => this.loadingPortal.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
